import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { claimsPermissions } from 'config/Auth.Config';
import Claim from 'models/claims/claim/Model.Claim';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimEmailTemplate from 'models/core/claimEmailTemplate/Model.ClaimEmailTemplate';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import VehicleMake from 'models/productManagement/vehicleMake/Model.VehicleMake';
import { ClaimAction } from './Claim.Hooks';
import { claimJobIndexRoute } from './ClaimJob.Index';

export function useClaimJobRouter() {
  return usePageRouter<Module.Claims.ClaimJob.Params, Module.Claims.ClaimJob.Query>({ route: claimJobIndexRoute });
}
export default function useClaimJob() {
  const pageRouter = useClaimJobRouter();
  const emailTemplatesApi = useApi({ action: ClaimEmailTemplate.list });
  const emailTemplates = emailTemplatesApi.payload?.data;

  const statusInfoApi = useApi({ action: Claim.getStatusFlowAndRequirements });
  const statusInfo = statusInfoApi.payload;

  const { user, permissions, currentPermissions, userId } = useAuthStore();

  const isClaimAdmin = permissions?.CLAIM_ADMIN;
  const isEditable = isClaimAdmin || permissions?.EDIT_CLAIM;
  const canCreateClaim = isClaimAdmin || permissions?.CREATE_CLAIM;
  const canCreateSimpleClaim = isClaimAdmin || permissions?.CREATE_SIMPLE_CLAIM;
  const canAddClaim = isClaimAdmin || permissions?.CREATE_NEW_CLAIM;
  const hasAnyPermission = claimsPermissions.some((it) => currentPermissions.includes(it));

  const claimId = pageRouter?.params?.claimId ? parseInt(pageRouter?.params?.claimId) : undefined;

  const vehicleMakeApi = useApi({ action: VehicleMake.list, wait: true });
  const userApi = useApi({ action: User.list, default: { isDisabled: false }, wait: true });
  const dealersApi = useApi({
    action: Dealer.list,
    default: {
      id: user?.dealers?.map((d) => d.id) ?? [],
      distributorId: user?.distributorId ? [user?.distributorId] : undefined,
    },
    wait: true,
  });

  //#region Claim Jobs
  const listApi = useApi(
    {
      action: ClaimJob.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user?.distributorId ? [user?.distributorId] : undefined,
        claimReporterId: permissions?.VIEW_ONLY_OWNED_CLAIMS ? user?.id : undefined,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !hasAnyPermission,
    },
    [JSON.stringify(pageRouter.query.list)]
  );
  //#endregion

  const claimListApi = useApi(
    {
      action: Claim.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        reporterId: permissions?.VIEW_ONLY_OWNED_CLAIMS ? user?.id : undefined,
        dealerId: user?.dealers?.map((d) => d.id) ?? [],
        distributorId: user?.distributorId ? [user?.distributorId] : undefined,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !hasAnyPermission,
    },
    [JSON.stringify(pageRouter.query.list)]
  );
  //#region Variables
  const claim = claimListApi.payload?.data?.find((it) => it.id === claimId);
  const isDeletable =
    !!(isEditable && claim && !claim.claimJobCount && !claim.claimDocuments?.length) &&
    (isClaimAdmin || permissions?.DELETE_CLAIM);
  const isClaimOwner = claim?.ownerId === userId;
  //#endregion

  return {
    hasAnyPermission,
    isEditable,
    canCreateClaim,
    canCreateSimpleClaim,
    canAddClaim,
    isClaimAdmin,
    isClaimOwner,
    statusInfo,
    emailTemplates,
    claim,
    dealersApi,
    vehicleMakeApi,
    userApi,
    isDeletable,
    views: {
      [ClaimAction.DELETE]: pageRouter.params.action === ClaimAction.DELETE && isDeletable,
      [ClaimAction.EDIT_SIMPLE]: pageRouter.params.action === ClaimAction.EDIT_SIMPLE && isEditable,
    },
    listApi,
    pageRouter,
  };
}

export function useClaimJobProvider() {
  return useDataProvider<ReturnType<typeof useClaimJob>>();
}
